import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchUser } from '../../actions/auth.actions';
import {
  ADMIN_CONTACT_CITY_ROLES_OPTIONS,
  ADMIN_CONTACT_PARTNER,
  ADMIN_CONTACT_PARTNER_ROLES_OPTIONS,
  ADMIN_CONTACT_SCHOOL,
  ADMIN_CONTACT_SCHOOL_ROLES_OPTIONS
} from '../../constants/admin.contacts.constants';
import { ROOT_PATH } from '../../constants/paths.constants';
import { ALL_SELECT_OPTION } from '../../constants/settings.constants';
import formValidators from '../../form-validators';
import { useDependency } from '../../hooks/useDependency.hook';
import { useForm } from '../../hooks/useForm.hook';
import { getQueryParams } from '../../lib/route.utils';
import { authenticatedSelector, currentUserSelector } from '../../selectors/auth.selectors';
import adminSchoolsService from '../../services/api/admin.schools.service';
import authService from '../../services/api/auth.service';
import partnersService from '../../services/api/partners.service';
import FirstTimeForm from './first-time-setup/FirstTimeForm';

const LoginFirstTimeSetup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = getQueryParams(location.search || '');
  const authenticated = useSelector(authenticatedSelector);
  const user = useSelector(currentUserSelector);
  const [emailValidation, setEmailValidation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [hideTypeSelection, setHideTypeSelection] = useState(false);
  const organizations = useDependency(partnersService.getPartnersOptions);
  const nets = useDependency(adminSchoolsService.getSchoolNetsOptions);
  const schools = useDependency(adminSchoolsService.getAllSchools);
  const { data, input } = useForm({
    ...user,
    organization: '',
    nets: [],
    schools: [],
    branches: [],
    type: '',
    acceptPrivacy: false,
    overwriteRoles: false
  });
  const schoolsData = schools.options.filter((s) => data?.nets?.includes(s.netId));
  const allBranches = _.flatten(schools.options?.map((s) => s?.branches?.map((b) => ({ ...b, schoolId: s.id })) ?? []));
  const branchesData = _.flatten(
    schoolsData
      .filter((s) => data?.schools?.[0] === ALL_SELECT_OPTION.value || data?.schools?.includes(s.id))
      ?.map((s) => s.branches ?? [])
  );
  const shouldFillSchoolBranches = data?.branches?.some((b) => b?.allFromSchool);

  const getCanConfirm = () => {
    const hasValidEmail = !!data?.email && !emailValidation;
    const hasValidSchoolPart =
      data?.type === ADMIN_CONTACT_SCHOOL &&
      data?.nets?.length > 0 &&
      data?.schools?.length > 0 &&
      data?.branches?.length > 0;
    const hasValidPartnerPart = data?.type === ADMIN_CONTACT_PARTNER && !!data?.organization;
    const hasValidName = !!data?.firstName && !!data?.lastName;
    return (
      hasValidEmail &&
      hasValidName &&
      !!data?.type &&
      !!data?.acceptPrivacy &&
      (hasValidSchoolPart || hasValidPartnerPart)
    );
  };

  const handleConfirm = () => {
    setSubmitError('');
    setSubmitting(true);
    authService.submitFirstTimeSetup(data, {
      onError: (message) => {
        setSubmitting(false);
        setSubmitError(message);
      },
      onSuccess: () => {
        dispatch(
          fetchUser(() => {
            setSubmitting(false);
            return navigate(params?.fromUrl ?? ROOT_PATH);
          })
        );
      }
    });
  };

  const handleTypeChange = (value) => {
    input(true, 'overwriteRoles');
    input(value, 'type');
  };

  const handleNetChange = (netIds) => {
    input(true, 'overwriteRoles');
    const filteredSchoolIds = data?.schools?.filter((schoolId) =>
      netIds?.includes(schools.options?.find((s) => s.id === schoolId)?.netId)
    );
    handleSchoolChange(filteredSchoolIds);
    input(netIds, 'nets');
  };

  const handleSchoolChange = (schoolIds) => {
    input(true, 'overwriteRoles');
    const newBranches = data?.branches?.filter((branchId) => {
      const branchSchoolId = allBranches.find((b) => b.id === branchId)?.schoolId;
      return schoolIds?.[0] === ALL_SELECT_OPTION.value || schoolIds.includes(branchSchoolId);
    });
    input(newBranches, 'branches');
    input(schoolIds, 'schools');
  };

  const handleBranchChange = (value) => {
    input(true, 'overwriteRoles');
    input(value, 'branches');
  };

  const handleOrganizationChange = (value) => {
    input(true, 'overwriteRoles');
    input(value, 'organization');
  };

  const handleEmailChange = (value) => {
    const validationError = formValidators.email(value);
    setEmailValidation(validationError);
    input(value, 'email');
  };

  const prefillUserTypeAndOrganisation = () => {
    const permission = user?.permissionsWithTreepaths?.[0] ?? null;
    if (
      (!permission || ADMIN_CONTACT_CITY_ROLES_OPTIONS.map((opt) => opt.value).includes(permission?.role)) &&
      !user?.type
    ) {
      setIsPrefilled(true);
      return;
    }

    if (ADMIN_CONTACT_PARTNER_ROLES_OPTIONS.map((opt) => opt.value).includes(permission?.role)) {
      setHideTypeSelection(true);
      input(ADMIN_CONTACT_PARTNER, 'type');
      input(permission?.treePath, 'organization');
      setIsPrefilled(true);
      return;
    }

    if (ADMIN_CONTACT_SCHOOL_ROLES_OPTIONS.map((opt) => opt.value).includes(permission?.role)) {
      setHideTypeSelection(true);
      input(ADMIN_CONTACT_SCHOOL, 'type');
      const schoolRoles = user?.permissionsWithTreepaths?.filter((p) =>
        ADMIN_CONTACT_SCHOOL_ROLES_OPTIONS.map((opt) => opt.value).includes(p?.role)
      );
      const mappedToSchools = schoolRoles
        ?.map((r) => ({
          net: r?.treePath?.split('#')?.[0] ?? '',
          school: r?.treePath?.split('#')?.[3] ?? '',
          branch: r?.treePath?.split('#')?.[4] ?? ALL_SELECT_OPTION.value
        }))
        ?.filter((i) => i.net && i.school);
      const nets = mappedToSchools?.map((i) => i.net);
      const schools = mappedToSchools?.map((i) => i.school);
      const branches = _.flatten(
        mappedToSchools?.map((i) => {
          if (i.branch === ALL_SELECT_OPTION.value) {
            return { allFromSchool: i.school };
          }
          return i.branch;
        })
      );
      input(nets, 'nets');
      input(schools, 'schools');
      input(branches, 'branches');
      setIsPrefilled(true);

      return;
    }

    if (user?.type && [ADMIN_CONTACT_PARTNER, ADMIN_CONTACT_SCHOOL].includes(user.type)) {
      setHideTypeSelection(true);
      input(user?.type, 'type');
      setIsPrefilled(true);

      return;
    }
  };

  useEffect(() => {
    if (data?.type === ADMIN_CONTACT_SCHOOL) {
      nets.onLoad();
      schools.onLoad();
    }
    if (data?.type === ADMIN_CONTACT_PARTNER) {
      organizations.onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.type]);

  useEffect(() => {
    if (!user?.isFirstLogin) {
      navigate(ROOT_PATH);
    }
  }, [navigate, user?.isFirstLogin]);

  useEffect(() => {
    prefillUserTypeAndOrganisation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (schools.isLoaded && shouldFillSchoolBranches) {
      const filledBranches = _.flatten(
        data?.branches?.map((b) => {
          if (b?.allFromSchool) {
            return data?.schools?.length > 1
              ? allBranches
                  ?.filter((branchOption) => branchOption.schoolId === b?.allFromSchool)
                  ?.map((branchOption) => branchOption.id)
              : ALL_SELECT_OPTION.value;
          }
          return b;
        })
      );
      input(filledBranches, 'branches');
    }
  }, [input, data?.branches, data?.schools?.length, allBranches, schools.isLoaded, shouldFillSchoolBranches]);

  useEffect(() => {
    if (!authenticated) {
      return navigate(ROOT_PATH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="spo-p-first-time u-margin-top-md u-margin-bottom-md">
      <div className="spo-p-first-time__content">
        <h3 className="u-padding u-bg-white">Welkom</h3>
        <div className="spo-p-first-time__content-form u-bg-white u-padding">
          <div className="row">
            <div className="col-xs-12 col-lg-8">
              <p className="u-margin-bottom-lg">
                Het lijkt erop dat dit jouw eerste keer is dat je het Samenwerkingsplatform Onderwijs bezoekt. Kijk
                hieronder jouw gegevens even na en zorg ervoor dat je een geldig e-mailadres gebruikt waarop we jou
                kunnen contacteren. De beheerder van de organisatie waartoe je behoort zal je toegang bezorgen.
              </p>
            </div>
          </div>
          {isPrefilled && (
            <FirstTimeForm
              hideTypeSelection={hideTypeSelection}
              data={data}
              onInput={input}
              onEmailInput={handleEmailChange}
              submitError={submitError}
              submitting={submitting}
              emailValidation={emailValidation}
              organizations={organizations}
              nets={nets}
              schools={schools}
              schoolsData={schoolsData}
              branchesData={branchesData}
              onNetChange={handleNetChange}
              onSchoolChange={handleSchoolChange}
              onBranchChange={handleBranchChange}
              onOrganizationChange={handleOrganizationChange}
              onTypeChange={handleTypeChange}
              canConfirm={getCanConfirm()}
              onConfirm={handleConfirm}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginFirstTimeSetup;
